import {
  Component, OnInit, OnDestroy, inject,
  ViewChild, ElementRef, AfterViewChecked
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatService, ChatMessage } from '../../../core/services/chat.service';
import { ConversationService, Conversation, ConversationMessage } from '../../../core/services/conversation.service';
import { MemberService, Member } from '../../../core/services/member.service';
import { AuthService } from '../../../core/auth/auth.service';
import { SpaceContextService } from '../../../core/services/space-context.service';
import { UnreadService } from '../../../core/services/unread.service';
import { TranslatePipe } from '@ngx-translate/core';

const POLL_MS = 8000;

type View = 'list' | 'global' | 'private';

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule],
  template: `
    <!-- FAB -->
    <button class="fab" (click)="toggle()" [class.open]="open" aria-label="Chat">
      <span class="material-icons">{{ open ? 'close' : 'chat' }}</span>
      <span class="fab-badge" *ngIf="!open && unreadSvc.chatCount() > 0">{{ unreadSvc.chatCount() > 9 ? '9+' : unreadSvc.chatCount() }}</span>
    </button>

    <!-- Panel -->
    <div class="chat-panel" [class.visible]="open">

      <!-- ══ VISTA: LISTA ══ -->
      <ng-container *ngIf="view === 'list'">
        <div class="panel-header">
          <span class="material-icons panel-icon">chat</span>
          <span class="panel-title">Mensajes</span>
          <button class="btn-icon" (click)="showModal = true" title="Nueva conversación">
            <span class="material-icons">edit</span>
          </button>
          <button class="btn-icon" (click)="open = false">
            <span class="material-icons">remove</span>
          </button>
        </div>

        <div class="list-search">
          <input [(ngModel)]="searchQ" placeholder="Buscar...">
        </div>

        <div class="panel-body no-pad">
          <!-- Chat global siempre arriba -->
          <div class="list-item" (click)="openGlobal()" *ngIf="!searchQ || 'chat del club'.includes(searchQ.toLowerCase())">
            <div class="item-av global"><span class="material-icons" style="font-size:20px">groups</span></div>
            <div class="item-info">
              <div class="item-name">Chat del club</div>
              <div class="item-sub">Todos los miembros</div>
            </div>
          </div>

          <!-- Conversaciones privadas -->
          <div class="list-item" *ngFor="let c of filteredConvs" (click)="openPrivate(c)">
            <div class="item-av" [class.group]="c.type === 'GROUP'">
              <span class="material-icons" *ngIf="c.type === 'GROUP'" style="font-size:18px">group</span>
              <span *ngIf="c.type === 'DIRECT'">{{ c.name?.[0]?.toUpperCase() }}</span>
            </div>
            <div class="item-info">
              <div class="item-name">{{ c.name }}</div>
              <div class="item-sub" *ngIf="c.type === 'GROUP'">{{ c.members.length }} miembros</div>
              <div class="item-sub" *ngIf="c.type === 'DIRECT'">Mensaje directo</div>
            </div>
          </div>

          <div class="list-empty" *ngIf="filteredConvs.length === 0 && !searchQ">
            <span class="material-icons">chat_bubble_outline</span>
            <p>Usá el lápiz para chatear</p>
          </div>
        </div>
      </ng-container>

      <!-- ══ VISTA: CHAT GLOBAL ══ -->
      <ng-container *ngIf="view === 'global'">
        <div class="panel-header">
          <button class="btn-icon" (click)="goList()"><span class="material-icons">arrow_back</span></button>
          <div class="hd-av global"><span class="material-icons" style="font-size:16px">groups</span></div>
          <div class="hd-info">
            <div class="hd-name">Chat del club</div>
            <div class="hd-sub">Todos los miembros</div>
          </div>
          <button class="btn-icon" (click)="open = false"><span class="material-icons">remove</span></button>
        </div>
        <div class="panel-body" #scrollGlobal>
          <div *ngIf="loadingGlobal" class="loading"><span class="material-icons spin">autorenew</span></div>
          <ng-container *ngIf="!loadingGlobal">
            <div *ngFor="let m of globalMsgs; trackBy: trackById" class="msg-row" [class.mine]="m.userId === myId">
              <div class="av" *ngIf="m.userId !== myId">{{ m.userName[0]?.toUpperCase() }}</div>
              <div class="bubble-col">
                <div class="sender" *ngIf="m.userId !== myId">{{ m.userName }}</div>
                <div class="bubble" [class.mine]="m.userId === myId">
                  {{ m.content }}
                  <button class="del" *ngIf="m.userId === myId" (click)="delGlobal(m)">
                    <span class="material-icons">close</span>
                  </button>
                </div>
                <div class="ts" [class.mine]="m.userId === myId">{{ fmt(m.createdAt) }}</div>
              </div>
            </div>
            <div class="list-empty" *ngIf="globalMsgs.length === 0">
              <span class="material-icons">chat_bubble_outline</span><p>Sé el primero 👋</p>
            </div>
          </ng-container>
        </div>
        <div class="panel-footer">
          <input [(ngModel)]="draft" placeholder="Escribí un mensaje..." (keydown.enter)="sendGlobal()" [disabled]="sending" maxlength="500">
          <button class="send" (click)="sendGlobal()" [disabled]="!draft.trim() || sending">
            <span class="material-icons">send</span>
          </button>
        </div>
      </ng-container>

      <!-- ══ VISTA: CHAT PRIVADO ══ -->
      <ng-container *ngIf="view === 'private' && activeConv">
        <div class="panel-header">
          <button class="btn-icon" (click)="goList()"><span class="material-icons">arrow_back</span></button>
          <div class="hd-av" [class.group]="activeConv.type === 'GROUP'">
            <span class="material-icons" *ngIf="activeConv.type === 'GROUP'" style="font-size:16px">group</span>
            <span *ngIf="activeConv.type === 'DIRECT'">{{ activeConv.name?.[0]?.toUpperCase() }}</span>
          </div>
          <div class="hd-info">
            <div class="hd-name">{{ activeConv.name }}</div>
            <div class="hd-sub" *ngIf="activeConv.type === 'GROUP'">{{ activeConv.members.length }} miembros</div>
          </div>
          <button class="btn-icon" (click)="open = false"><span class="material-icons">remove</span></button>
        </div>
        <div class="panel-body" #scrollPrivate>
          <div *ngIf="loadingPrivate" class="loading"><span class="material-icons spin">autorenew</span></div>
          <ng-container *ngIf="!loadingPrivate">
            <div *ngFor="let m of privateMsgs; trackBy: trackByMsgId" class="msg-row" [class.mine]="m.userId === myId">
              <div class="av" *ngIf="m.userId !== myId">{{ m.userName[0]?.toUpperCase() }}</div>
              <div class="bubble-col">
                <div class="sender" *ngIf="m.userId !== myId && activeConv.type === 'GROUP'">{{ m.userName }}</div>
                <div class="bubble" [class.mine]="m.userId === myId">{{ m.content }}</div>
                <div class="ts" [class.mine]="m.userId === myId">{{ fmt(m.createdAt) }}</div>
              </div>
            </div>
            <div class="list-empty" *ngIf="privateMsgs.length === 0">
              <span class="material-icons">chat_bubble_outline</span><p>Mandá el primer mensaje 👋</p>
            </div>
          </ng-container>
        </div>
        <div class="panel-footer">
          <input [(ngModel)]="draft" placeholder="Escribí un mensaje..." (keydown.enter)="sendPrivate()" [disabled]="sending" maxlength="500">
          <button class="send" (click)="sendPrivate()" [disabled]="!draft.trim() || sending">
            <span class="material-icons">send</span>
          </button>
        </div>
      </ng-container>

      <!-- ══ MODAL: NUEVA CONVERSACIÓN ══ -->
      <div class="modal-overlay" *ngIf="showModal" (click)="closeModal()">
        <div class="modal" (click)="$event.stopPropagation()">
          <div class="modal-hd">
            <span style="font-size:14px;font-weight:700;">Nueva conversación</span>
            <button class="btn-icon sm" (click)="closeModal()"><span class="material-icons">close</span></button>
          </div>
          <div class="modal-tabs">
            <button [class.active]="modalType === 'DIRECT'" (click)="modalType = 'DIRECT'">
              <span class="material-icons" style="font-size:16px">person</span> Directo
            </button>
            <button [class.active]="modalType === 'GROUP'" (click)="modalType = 'GROUP'">
              <span class="material-icons" style="font-size:16px">group</span> Grupo
            </button>
          </div>
          <div class="modal-body">
            <input *ngIf="modalType === 'GROUP'" class="modal-input" [(ngModel)]="groupName" placeholder="Nombre del grupo">
            <input class="modal-input" [(ngModel)]="memberSearch" placeholder="Buscar miembro...">
            <div class="member-list">
              <div class="member-item" *ngFor="let m of filteredModalMembers"
                   [class.selected]="isSelected(m)"
                   (click)="modalType === 'DIRECT' ? quickDirect(m) : toggleMember(m)">
                <div class="m-av" [class.sel]="isSelected(m)">
                  <span class="material-icons" *ngIf="isSelected(m)" style="font-size:16px">check</span>
                  <span *ngIf="!isSelected(m)">{{ m.fullName[0]?.toUpperCase() }}</span>
                </div>
                <span style="font-size:13px;">{{ m.fullName }}</span>
              </div>
            </div>
            <button *ngIf="modalType === 'GROUP'" class="btn-create"
                    [disabled]="!groupName.trim() || selectedMembers.length === 0 || saving"
                    (click)="createGroup()">
              {{ saving ? 'Creando...' : 'Crear grupo' }}
            </button>
          </div>
        </div>
      </div>

    </div><!-- /panel -->
  `,
  styles: [`
    .fab {
      position: fixed; bottom: calc(72px + env(safe-area-inset-bottom)); right: 16px; z-index: 500;
      width: 52px; height: 52px; border-radius: 50%; background: var(--color-accent);
      color: #fff; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center; transition: transform .2s;
      .material-icons { font-size: 24px; }
      &.open { background: #475569; }
      &:active { transform: scale(.93); }
    }
    .fab-badge { position: absolute; top: 2px; right: 2px; background: #ef4444; color: #fff;
      font-size: 9px; font-weight: 700; min-width: 16px; height: 16px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center; padding: 0 3px; pointer-events: none; }

    .chat-panel {
      position: fixed; bottom: calc(132px + env(safe-area-inset-bottom)); right: 16px; z-index: 499;
      width: min(360px, calc(100vw - 32px)); height: min(500px, calc(100dvh - 200px));
      background: #fff; border: 1px solid var(--color-border); border-radius: 18px;
      box-shadow: 0 8px 32px rgba(0,0,0,.18);
      display: flex; flex-direction: column; overflow: hidden;
      opacity: 0; pointer-events: none; transform: translateY(12px) scale(.97); transform-origin: bottom right;
      transition: opacity .18s ease, transform .18s ease;
      &.visible { opacity: 1; pointer-events: all; transform: none; }
    }

    .panel-header { display: flex; align-items: center; gap: 8px; padding: 10px 12px;
      border-bottom: 1px solid var(--color-border); background: #fff; flex-shrink: 0; }
    .panel-icon { font-size: 18px; color: var(--color-accent); }
    .panel-title { flex: 1; font-size: 14px; font-weight: 700; color: var(--color-text); }
    .btn-icon { background: none; border: none; cursor: pointer; color: var(--color-text-muted);
      display: flex; align-items: center; padding: 2px;
      .material-icons { font-size: 20px; }
      &:hover { color: var(--color-text); }
      &.sm .material-icons { font-size: 16px; }
    }

    .hd-av { width: 32px; height: 32px; border-radius: 50%; background: var(--color-accent);
      color: #fff; display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; flex-shrink: 0;
      &.global { background: #10b981; }
      &.group { background: #6366f1; }
    }
    .hd-info { flex: 1; min-width: 0; }
    .hd-name { font-size: 13px; font-weight: 700; color: var(--color-text); }
    .hd-sub { font-size: 11px; color: var(--color-text-muted); }

    .list-search { padding: 8px 12px; border-bottom: 1px solid var(--color-border);
      input { width: 100%; padding: 7px 12px; border: 1px solid var(--color-border); border-radius: 20px;
        font-size: 13px; background: var(--color-bg); outline: none; box-sizing: border-box;
        &:focus { border-color: var(--color-accent); }
      }
    }

    .panel-body { flex: 1; overflow-y: auto; display: flex; flex-direction: column;
      gap: 8px; padding: 10px 12px; scroll-behavior: smooth;
      &.no-pad { gap: 0; padding: 0; }
    }

    .list-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px;
      cursor: pointer; border-bottom: 1px solid var(--color-border); transition: background .12s;
      &:hover { background: #f9f9f9; }
      &:last-child { border-bottom: none; }
    }
    .item-av { width: 38px; height: 38px; border-radius: 50%; background: var(--color-accent);
      color: #fff; display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 15px; flex-shrink: 0;
      &.global { background: #10b981; }
      &.group { background: #6366f1; }
    }
    .item-info { flex: 1; min-width: 0; }
    .item-name { font-size: 13px; font-weight: 600; color: var(--color-text);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .item-sub { font-size: 11px; color: var(--color-text-muted); margin-top: 1px; }

    .list-empty { display: flex; flex-direction: column; align-items: center; gap: 8px;
      padding: 30px 16px; color: var(--color-text-muted); text-align: center;
      .material-icons { font-size: 36px; color: #ddd; }
      p { font-size: 13px; margin: 0; }
    }

    .loading { display: flex; justify-content: center; padding: 20px; }
    .spin { animation: spin 1s linear infinite; font-size: 28px; color: var(--color-accent); }
    @keyframes spin { to { transform: rotate(360deg); } }

    .msg-row { display: flex; align-items: flex-end; gap: 6px;
      &.mine { flex-direction: row-reverse; }
    }
    .av { width: 26px; height: 26px; border-radius: 50%; background: #e2e8f0; color: #475569;
      display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; flex-shrink: 0; }
    .bubble-col { display: flex; flex-direction: column; max-width: 80%; }
    .sender { font-size: 10px; font-weight: 600; color: var(--color-text-muted); margin-bottom: 2px; padding-left: 4px; }
    .bubble { position: relative; padding: 8px 12px; border-radius: 16px; font-size: 13px;
      line-height: 1.4; background: #fff; border: 1px solid var(--color-border); color: var(--color-text);
      word-break: break-word; border-radius: 16px 16px 16px 4px;
      &.mine { background: var(--color-accent); color: #fff; border-color: transparent;
               border-radius: 16px 16px 4px 16px; padding-right: 28px; }
    }
    .del { position: absolute; top: 4px; right: 4px; background: none; border: none;
      cursor: pointer; color: rgba(255,255,255,.7); padding: 0; opacity: 0;
      .material-icons { font-size: 12px; }
    }
    .bubble:hover .del { opacity: 1; }
    .ts { font-size: 9px; color: var(--color-text-muted); margin-top: 2px; padding: 0 4px;
      &.mine { text-align: right; }
    }

    .panel-footer { display: flex; gap: 8px; padding: 8px 12px; border-top: 1px solid var(--color-border);
      background: #fff; flex-shrink: 0;
      input { flex: 1; padding: 9px 13px; border-radius: 20px; border: 1px solid var(--color-border);
        background: var(--color-bg); color: var(--color-text); font-size: 13px; outline: none;
        &:focus { border-color: var(--color-accent); }
      }
    }
    .send { width: 36px; height: 36px; border-radius: 50%; border: none; background: var(--color-accent);
      color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
      .material-icons { font-size: 17px; }
      &:disabled { opacity: .4; cursor: default; }
    }

    /* Modal dentro del widget */
    .modal-overlay { position: absolute; inset: 0; background: rgba(0,0,0,.45); z-index: 10;
      display: flex; align-items: flex-end; border-radius: 18px; overflow: hidden; }
    .modal { background: #fff; width: 100%; border-radius: 16px 16px 0 0;
      display: flex; flex-direction: column; max-height: 90%; overflow: hidden; }
    .modal-hd { display: flex; align-items: center; justify-content: space-between;
      padding: 12px 14px; border-bottom: 1px solid var(--color-border); flex-shrink: 0; }
    .modal-tabs { display: flex; gap: 6px; padding: 8px 12px; flex-shrink: 0;
      button { flex: 1; padding: 8px; border: 1px solid var(--color-border); border-radius: 8px;
        background: #fff; cursor: pointer; font-size: 12px; display: flex; align-items: center;
        justify-content: center; gap: 4px; color: var(--color-text-muted);
        &.active { background: var(--color-accent); color: #fff; border-color: var(--color-accent); font-weight: 600; }
      }
    }
    .modal-body { flex: 1; overflow-y: auto; padding: 8px 12px 12px; display: flex; flex-direction: column; gap: 8px; }
    .modal-input { width: 100%; padding: 9px 12px; border: 1px solid var(--color-border); border-radius: 20px;
      font-size: 13px; outline: none; box-sizing: border-box;
      &:focus { border-color: var(--color-accent); }
    }
    .member-list { flex: 1; overflow-y: auto; max-height: 180px; }
    .member-item { display: flex; align-items: center; gap: 10px; padding: 8px 6px;
      cursor: pointer; border-radius: 8px; transition: background .12s;
      &:hover { background: #f5f5f5; }
      &.selected { background: #fef9ec; }
    }
    .m-av { width: 32px; height: 32px; border-radius: 50%; background: var(--color-accent);
      color: #fff; display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 13px; flex-shrink: 0;
      &.sel { background: #6366f1; }
    }
    .btn-create { width: 100%; padding: 11px; background: var(--color-accent); color: #fff;
      border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; margin-top: 4px;
      &:disabled { opacity: .45; cursor: default; }
    }
  `]
})
export class ChatWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scrollGlobal') private scrollGlobal!: ElementRef;
  @ViewChild('scrollPrivate') private scrollPrivate!: ElementRef;

  private chatSvc  = inject(ChatService);
  private convSvc  = inject(ConversationService);
  private memberSvc = inject(MemberService);
  private spaceSvc = inject(SpaceContextService);
  private auth     = inject(AuthService);
  protected unreadSvc = inject(UnreadService);

  // state
  open    = false;
  view: View = 'list';
  unread  = 0;
  myId    = '';
  draft   = '';
  sending = false;

  // lista
  conversations: Conversation[] = [];
  searchQ = '';

  // global
  globalMsgs: ChatMessage[]    = [];
  loadingGlobal = true;

  // privado
  activeConv: Conversation | null = null;
  privateMsgs: ConversationMessage[] = [];
  loadingPrivate = false;

  // modal
  showModal   = false;
  modalType: 'DIRECT' | 'GROUP' = 'DIRECT';
  memberSearch = '';
  groupName    = '';
  selectedMembers: Member[] = [];
  members: Member[] = [];
  saving = false;

  private globalTimer: any;
  private privateTimer: any;
  private doScroll = false;
  private globalLoaded = false;

  ngOnInit() {
    this.myId = this.auth.getCurrentUser()?.id ?? '';
    // carga silenciosa de datos
    this.spaceSvc.spaceId$.subscribe(id => {
      if (!id) return;
      this.loadGlobalSilent();
      this.loadConversations();
      this.loadMembers();
      this.globalTimer = setInterval(() => this.loadGlobalSilent(), POLL_MS);
    });
  }

  ngOnDestroy() {
    clearInterval(this.globalTimer);
    clearInterval(this.privateTimer);
  }

  ngAfterViewChecked() {
    if (this.doScroll) {
      const el = this.view === 'global' ? this.scrollGlobal : this.scrollPrivate;
      if (el) try { el.nativeElement.scrollTop = el.nativeElement.scrollHeight; } catch {}
      this.doScroll = false;
    }
  }

  toggle() {
    this.open = !this.open;
    if (this.open) {
      this.unread = 0;
      this.unreadSvc.markChatRead();
      this.loadConversations();
      if (this.view === 'global') this.doScroll = true;
      if (this.view === 'private') this.doScroll = true;
    }
  }

  // ── Navegación ──────────────────────────────────────────────

  openGlobal() {
    this.view = 'global';
    this.loadingGlobal = true;
    this.chatSvc.getMessages(this.spaceSvc.spaceId).subscribe({
      next: msgs => { this.globalMsgs = msgs; this.loadingGlobal = false; this.doScroll = true; },
      error: () => { this.loadingGlobal = false; }
    });
  }

  openPrivate(conv: Conversation) {
    this.activeConv = conv;
    this.view = 'private';
    this.loadingPrivate = true;
    this.convSvc.getMessages(this.spaceSvc.spaceId, conv.id).subscribe({
      next: msgs => { this.privateMsgs = msgs; this.loadingPrivate = false; this.doScroll = true; },
      error: () => { this.loadingPrivate = false; }
    });
    clearInterval(this.privateTimer);
    this.privateTimer = setInterval(() => this.pollPrivate(), POLL_MS);
    this.convSvc.markRead(this.spaceSvc.spaceId, conv.id).subscribe();
  }

  goList() {
    this.view = 'list';
    this.activeConv = null;
    clearInterval(this.privateTimer);
    this.draft = '';
    this.loadConversations();
  }

  // ── Cargas ──────────────────────────────────────────────────

  loadConversations() {
    this.convSvc.list(this.spaceSvc.spaceId).subscribe({
      next: c => { this.conversations = c; }
    });
  }

  loadGlobalSilent() {
    const prev = this.globalMsgs.length;
    this.chatSvc.getMessages(this.spaceSvc.spaceId).subscribe({
      next: msgs => {
        this.globalMsgs = msgs;
        this.loadingGlobal = false;
        if (this.view === 'global' && this.open) this.doScroll = true;
        // badge
        if (this.globalLoaded && (!this.open || this.view !== 'global')) {
          const newOnes = msgs.slice(prev).filter(m => m.userId !== this.myId);
          this.unread += newOnes.length;
        }
        this.globalLoaded = true;
      }
    });
  }

  pollPrivate() {
    if (!this.activeConv) return;
    this.convSvc.getMessages(this.spaceSvc.spaceId, this.activeConv.id).subscribe({
      next: msgs => {
        if (msgs.length !== this.privateMsgs.length) {
          this.privateMsgs = msgs;
          this.doScroll = true;
        }
      }
    });
  }

  loadMembers() {
    this.memberSvc.list(this.spaceSvc.spaceId, 'ACTIVE').subscribe({
      next: m => { this.members = m.filter(x => x.userId !== this.myId); }
    });
  }

  // ── Envío ────────────────────────────────────────────────────

  sendGlobal() {
    const txt = this.draft.trim();
    if (!txt || this.sending) return;
    this.draft = ''; this.sending = true;
    this.chatSvc.send(this.spaceSvc.spaceId, txt).subscribe({
      next: m => { this.globalMsgs.push(m); this.sending = false; this.doScroll = true; },
      error: () => { this.draft = txt; this.sending = false; }
    });
  }

  sendPrivate() {
    if (!this.activeConv) return;
    const txt = this.draft.trim();
    if (!txt || this.sending) return;
    this.draft = ''; this.sending = true;
    this.convSvc.sendMessage(this.spaceSvc.spaceId, this.activeConv.id, txt).subscribe({
      next: m => { this.privateMsgs.push(m); this.sending = false; this.doScroll = true; },
      error: () => { this.draft = txt; this.sending = false; }
    });
  }

  delGlobal(m: ChatMessage) {
    this.chatSvc.delete(this.spaceSvc.spaceId, m.id).subscribe({
      next: () => { this.globalMsgs = this.globalMsgs.filter(x => x.id !== m.id); }
    });
  }

  // ── Modal ────────────────────────────────────────────────────

  quickDirect(m: Member) {
    this.saving = true;
    this.convSvc.openDirect(this.spaceSvc.spaceId, m.userId).subscribe({
      next: conv => { this.closeModal(); this.loadConversations(); this.openPrivate(conv); },
      error: () => { this.saving = false; }
    });
  }

  createGroup() {
    if (!this.groupName.trim() || !this.selectedMembers.length) return;
    this.saving = true;
    this.convSvc.createGroup(this.spaceSvc.spaceId, this.groupName.trim(), this.selectedMembers.map(m => m.userId)).subscribe({
      next: conv => { this.closeModal(); this.loadConversations(); this.openPrivate(conv); },
      error: () => { this.saving = false; }
    });
  }

  closeModal() {
    this.showModal = false; this.modalType = 'DIRECT';
    this.memberSearch = ''; this.groupName = '';
    this.selectedMembers = []; this.saving = false;
  }

  isSelected(m: Member) { return this.selectedMembers.some(s => s.userId === m.userId); }
  toggleMember(m: Member) {
    this.selectedMembers = this.isSelected(m)
      ? this.selectedMembers.filter(s => s.userId !== m.userId)
      : [...this.selectedMembers, m];
  }

  // ── Filtros ──────────────────────────────────────────────────

  get filteredConvs(): Conversation[] {
    const q = this.searchQ.toLowerCase();
    return !q ? this.conversations : this.conversations.filter(c => c.name?.toLowerCase().includes(q));
  }

  get filteredModalMembers(): Member[] {
    const q = this.memberSearch.toLowerCase();
    return !q ? this.members : this.members.filter(m => m.fullName.toLowerCase().includes(q));
  }

  // ── Utils ────────────────────────────────────────────────────

  trackById(_: number, m: ChatMessage) { return m.id; }
  trackByMsgId(_: number, m: ConversationMessage) { return m.id; }

  fmt(ts: string): string {
    const d = new Date(ts), now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 86400000);
    const t = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    if (diff === 0) return t;
    if (diff === 1) return `ayer ${t}`;
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }) + ' ' + t;
  }
}
